import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useClientSocket } from './ClientSocketContext';

interface ApprovalEventsValue {
  epoch: number;
  changedApprovalId: number | null;
  invalidate: (approvalId?: number) => void;
}

const ApprovalEventsContext = createContext<ApprovalEventsValue>({
  epoch: 0,
  changedApprovalId: null,
  invalidate: () => undefined,
});

export const useApprovalEvents = () => useContext(ApprovalEventsContext);

export const ApprovalEventsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { subscribe, epoch: socketEpoch } = useClientSocket();
  const [epoch, setEpoch] = useState(0);
  const [changedApprovalId, setChangedApprovalId] = useState<number | null>(
    null,
  );
  const invalidate = useCallback((approvalId?: number) => {
    setChangedApprovalId(approvalId ?? null);
    setEpoch((value) => value + 1);
  }, []);

  useEffect(
    () =>
      subscribe((frame) => {
        if (frame.ch === 'approvals' && frame.op === 'changed') {
          invalidate(Number(frame.approvalId));
        }
      }),
    [invalidate, subscribe],
  );

  useEffect(() => {
    if (socketEpoch > 0) invalidate();
  }, [invalidate, socketEpoch]);

  useEffect(() => {
    const onFocus = () => invalidate();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [invalidate]);

  const value = useMemo(
    () => ({ epoch, changedApprovalId, invalidate }),
    [changedApprovalId, epoch, invalidate],
  );
  return (
    <ApprovalEventsContext.Provider value={value}>
      {children}
    </ApprovalEventsContext.Provider>
  );
};
